import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/formatting';
import { useAuth } from '../contexts/AuthContext';

// Helper function to get localized text from bilingual object
function getLocalizedText(textObj, lang = 'en') {
    if (typeof textObj === 'string') return textObj;
    if (typeof textObj === 'object' && textObj !== null) {
        return textObj[lang] || textObj.en || '';
    }
    return '';
}

function EventAttendeesModal({ isOpen, onClose, event, onSuccess }) {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language?.startsWith('tl') ? 'tl' : 'en';
    const { getAuthToken } = useAuth();

    const [attendees, setAttendees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [removeStatus, setRemoveStatus] = useState({});
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [attendeeToRemove, setAttendeeToRemove] = useState(null);

    const fetchEventData = useCallback(async () => {
        if (!event?.id) return;

        try {
            setIsLoading(true);
            setError(null);
            const response = await fetch(`/api/events/${event.id}`);
            if (!response.ok) {
                throw new Error('Failed to fetch event');
            }
            const data = await response.json();
            setAttendees(data.attendees || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [event?.id]);

    useEffect(() => {
        if (isOpen && event) {
            fetchEventData();
            setShowRemoveConfirm(false);
            setAttendeeToRemove(null);
            setRemoveStatus({});
        }
    }, [isOpen, event, fetchEventData]);

    if (!isOpen || !event) return null;

    function handleRemoveClick(attendee) {
        setAttendeeToRemove(attendee);
        setShowRemoveConfirm(true);
    }

    async function confirmRemoveAttendee() {
        if (!attendeeToRemove) return;

        const attendeeId = attendeeToRemove.userId || attendeeToRemove.phone;

        try {
            setRemoveStatus(prev => ({ ...prev, [attendeeId]: { loading: true } }));
            const token = await getAuthToken();

            const response = await fetch(`/api/events/${event.id}/signup/${encodeURIComponent(attendeeId)}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to remove attendee');
            }

            setRemoveStatus(prev => ({ ...prev, [attendeeId]: { success: true } }));
            setShowRemoveConfirm(false);
            setAttendeeToRemove(null);

            // Refresh attendees list
            fetchEventData();

            // Notify parent to refresh events list
            if (onSuccess) onSuccess();
        } catch (err) {
            setRemoveStatus(prev => ({ ...prev, [attendeeId]: { error: err.message } }));
        }
    }

    function formatSignupDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString(currentLang === 'tl' ? 'fil-PH' : 'en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    }

    function getAttendeeIdentifier(attendee) {
        return attendee.userId || attendee.phone;
    }

    const maxSlots = event.maxSlots || 5;
    const slotsUsed = attendees.length;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>×</button>
                <h3 className="modal-title">👥 {t('admin_manage_event')}</h3>
                <p className="modal-subtitle">{getLocalizedText(event.name, currentLang)}</p>

                {error && <div className="error-banner">{t('error')}: {error}</div>}

                {showRemoveConfirm && attendeeToRemove ? (
                    <div className="delete-confirm-container">
                        <div className="delete-confirm-icon">⚠️</div>
                        <h4>{t('admin_remove_confirm_title')}</h4>
                        <p>{t('admin_remove_confirm_message')}</p>
                        <p><strong>{attendeeToRemove.name || attendeeToRemove.email}</strong></p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn-outline-modern"
                                onClick={() => {
                                    setShowRemoveConfirm(false);
                                    setAttendeeToRemove(null);
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type="button"
                                className="btn-danger-modern"
                                onClick={confirmRemoveAttendee}
                            >
                                {t('admin_remove_confirm_btn')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Event Details Summary */}
                        <div className="modal-event-summary">
                            <div className="event-summary-row">
                                <span className="summary-label">{t('when')}</span>
                                <span className="summary-value">
                                    {event.start_time ? formatDate(event.start_time, currentLang) : 'TBD'}
                                </span>
                            </div>
                            <div className="event-summary-row">
                                <span className="summary-label">{t('where')}</span>
                                <span className="summary-value">
                                    {event.place?.name || event.place || 'TBD'}
                                </span>
                            </div>
                        </div>

                        {/* Slots indicator */}
                        <div className={`slots-indicator modal-slots ${slotsUsed >= maxSlots ? 'full' : ''}`}>
                            <span className="slots-icon">🎟️</span>
                            <span className="slots-text">
                                {slotsUsed} / {maxSlots} {t('admin_slots_filled')}
                            </span>
                            <div className="slots-bar">
                                <div
                                    className="slots-bar-fill"
                                    style={{ width: `${Math.min((slotsUsed / maxSlots) * 100, 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Attendees Section */}
                        <div className="modal-attendees-section">
                            <h4 className="attendees-section-title">
                                {t('admin_attendees')} ({attendees.length})
                            </h4>

                            {isLoading ? (
                                <div className="modal-loading">
                                    <div className="loading-spinner small"></div>
                                    <span>{t('loading')}</span>
                                </div>
                            ) : attendees.length === 0 ? (
                                <div className="empty-state small">
                                    <p>{t('admin_no_attendees')}</p>
                                </div>
                            ) : (
                                <div className="attendees-list modal-list">
                                    {attendees.map((attendee, index) => {
                                        const attendeeId = getAttendeeIdentifier(attendee);
                                        const status = removeStatus[attendeeId];

                                        return (
                                            <div key={attendeeId || index} className="attendee-item compact">
                                                <div className="attendee-info">
                                                    <div className="attendee-primary">
                                                        {attendee.isAnonymous ? (
                                                            <>
                                                                <span className="attendee-name">{attendee.name}</span>
                                                                <span className="attendee-badge guest">{t('admin_attendee_anonymous')}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="attendee-name">{attendee.email}</span>
                                                                <span className="attendee-badge registered">{t('admin_attendee_authenticated')}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="attendee-secondary">
                                                        {attendee.phone && (
                                                            <a
                                                                href={`https://wa.me/${attendee.phone.replace(/\D/g, '')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="attendee-phone"
                                                                title={t('phone_whatsapp_title')}
                                                            >
                                                                📱 {attendee.phoneFormatted || attendee.phone}
                                                            </a>
                                                        )}
                                                        <span className="attendee-signup-date">
                                                            {t('admin_signed_up_at')}: {formatSignupDate(attendee.signedUpAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="attendee-actions">
                                                    {status?.success ? (
                                                        <span className="success-badge small">✓</span>
                                                    ) : status?.error ? (
                                                        <span className="error-text small">{status.error}</span>
                                                    ) : (
                                                        <button
                                                            className="btn-danger-small"
                                                            onClick={() => handleRemoveClick(attendee)}
                                                            disabled={status?.loading}
                                                        >
                                                            {status?.loading ? t('admin_removing') : t('admin_remove_attendee')}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Close button */}
                        <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                            <button
                                type="button"
                                className="btn-primary-modern"
                                onClick={onClose}
                            >
                                {t('close')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default EventAttendeesModal;
